import React, { useEffect, useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { COLORS } from './constants';
import { MB_Blur, MB_BLUR_TINT } from '@mightybyte/rnw.components.blur';
import { mbPlatformStyle, mbApplyTransparency } from '@mightybyte/rnw.utils.style-utils';


interface MB_ModalUtilBackdropProps {
    /**
     * If true, the backdrop will be disabled and no blur will be applied.
     */
    disableBackdropBlur?: boolean,

    /**
     * A custom backdrop to use instead of the default blur.
     */
    customBackdrop?: React.ReactNode,

    /**
     * A number from 1 to 100 to control the intensity of the blur effect.
     */
    backdropBlurIntensity?: number,

    /**
     * A tint mode which will be applied to the view.
     */
    backdropBlurTint?: MB_BLUR_TINT,

    /**
     * If true, the modal will close when the backdrop is pressed.
     */
    closeOnBackdropPress?: boolean,

    /**
     * A callback that will be called when the backdrop is pressed.
     */
    onBackdropPress?: () => void,

    /**
     * The color of the backdrop.
     */
    backdropColor?: string,

    /**
     * The opacity of the backdrop.
     */
    backdropOpacity?: number,

    /**
     * A callback that will be called when the modal is dismissed.
     */
    onDismissInProgress: () => void,
}

const MB_ModalUtilBackdrop = (props: MB_ModalUtilBackdropProps) => {
    const backgroundColor = props.customBackdrop || props.disableBackdropBlur ? 'transparent' : mbApplyTransparency(props.backdropColor ?? COLORS.popUpBlurBackground, props.backdropOpacity ?? 0.7);

    const [idForForceUpdate, setIdForForceUpdate] = useState(1);


    // TODO: Note: With certain animations like zoom in, the blur stops working if we try to render it right away. We end up needing to
    // fore re-render it a bit late after the view is not super small in order to show the blur.
    useEffect(() => {
        setTimeout(() => setIdForForceUpdate(old => old + 1), 50);
        setTimeout(() => setIdForForceUpdate(old => old + 1), 100);
    }, []);

    return (
        <TouchableOpacity
            disabled={!props.closeOnBackdropPress && !props.onBackdropPress}
            activeOpacity={1}
            style={[styles.customBackdropTouchable, { backgroundColor }]}
            onPress={() => {
                if (props.closeOnBackdropPress) {
                    props.onDismissInProgress();
                }
                props.onBackdropPress?.();
            }}
        >
            {!props.customBackdrop && !props.disableBackdropBlur &&
                <MB_Blur
                    key={idForForceUpdate}
                    intensity={props.backdropBlurIntensity}
                    tint={props.backdropBlurTint ?? MB_BLUR_TINT.default}
                    androidOverlayColor={COLORS.androidBlurViewOverlayColor}
                    style={[{ width: '100%', height: '100%' }]}
                />
            }
        </TouchableOpacity>

    );
};

export { MB_ModalUtilBackdrop };
export type { MB_ModalUtilBackdropProps };

const styles = StyleSheet.create({
    customBackdropTouchable: {
        height: '100%',
        width: '100%',
        position: 'absolute',
        ...mbPlatformStyle({
            web: {
                cursor: 'default',
                overflow: 'hidden', // for safari, otherwise modals that are scrollable will have the blur applied to the top of the modal for some reason.
            },
        }),
    },
});
